import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectTrigger } from "@activecollab/components";
import PropTypes from "prop-types";
import SelectDateUserDaysOff from "~/components/SelectDate/SelectDateUserDaysOff";
import Angie from "~/utils/angie";

const SelectDates = ({ userId, selectedDays: defaultSelectedDays, onChange, changeMode, target, ...props }) => {
  const { t } = useTranslation();
  const { from, to } = defaultSelectedDays;
  const [open, setOpen] = useState(false);

  const selectedDays = useMemo(() => {
    if (from && to) {
      return { from, to };
    } else if (to) {
      return { from: to, to };
    }
    return undefined;
  }, [from, to]);

  const handleSave = useCallback(
    (values) => {
      if (onChange) {
        onChange({ from: values?.from || null, to: values?.to || null });
      }
    },
    [onChange]
  );

  const onDayClick = useCallback((_, modifiers) => {
    if (modifiers["c-DayPicker-Day--disabled"]) {
      Angie.logger.info("Unavailable day clicked", {
        clicked_from: "due_date",
        disabled_day_type: modifiers["c-DayPicker-Day--weekend"]
          ? "weekend"
          : modifiers["c-DayPicker-Day--nonWorkingDay"]
          ? "non-working-day"
          : "disabled",
      });
    }
  }, []);

  const trigger = useCallback(
    (dates) => {
      if (target) return target;

      const hasDates = dates !== t("Set...");

      return (
        <SelectTrigger
          typographyProps={{
            color: hasDates ? "primary" : "quaternary",
          }}
          className="tw-w-full"
        >
          {dates}
        </SelectTrigger>
      );
    },
    [target, t]
  );

  return (
    <SelectDateUserDaysOff
      trigger={trigger}
      userId={open ? userId : 0}
      selectedDays={selectedDays}
      onSave={handleSave}
      onDayClick={onDayClick}
      changeMode={changeMode}
      defaultLabelText={`${t("Set")}...`}
      onToggleDatePicker={setOpen}
      {...props}
    />
  );
};

SelectDates.propTypes = {
  userId: PropTypes.number,
  selectedDays: PropTypes.shape({
    from: PropTypes.number,
    to: PropTypes.number,
  }),
  target: PropTypes.element,
  onChange: PropTypes.func,
  changeMode: PropTypes.string,
};

SelectDates.displayName = "SelectDates";

export default SelectDates;
